import React, { createContext, useContext, useState, useEffect } from 'react';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate checking for a logged-in user
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error("Failed to parse user from localStorage", error);
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (email, password) => {
    // Mock login logic with enhanced role assignment
    setLoading(true);
    return new Promise((resolve) => {
      setTimeout(() => {
        let userData = {
          name: 'Dr. William Gray',
          email: 'wgray@stloralsurgery.com',
          role: 'SuperAdmin',
          avatar: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=150&h=150&fit=crop&crop=face',
          permissions: ['all'], // SuperAdmin gets all permissions
        };

        // Enhanced role assignment based on email patterns
        if (email.toLowerCase().includes('superadmin') || 
            email.toLowerCase() === 'wgray@stloralsurgery.com' ||
            email.toLowerCase() === 'admin@referbridge.com') {
          userData.role = 'SuperAdmin';
          userData.name = 'Dr. William Gray';
          userData.permissions = ['all'];
        } else if (email.toLowerCase().includes('admin')) {
          userData.role = 'Admin';
          userData.name = 'Admin User';
          userData.permissions = ['internal', 'dealrooms'];
        } else if (email.toLowerCase().includes('patient')) {
          userData.role = 'Patient';
          userData.name = 'Patient User';
          userData.permissions = ['patient', 'public', 'lms', 'blog', 'community'];
        } else if (email.toLowerCase().includes('provider') || 
                   email.toLowerCase().includes('doctor') ||
                   email.toLowerCase().includes('dr.')) {
          userData.role = 'Provider';
          userData.name = 'Dr. Provider';
          userData.permissions = ['providers', 'dealrooms', 'public', 'lms', 'blog', 'community'];
        } else if (email.toLowerCase().includes('referral')) {
          userData.role = 'ReferralProvider';
          userData.name = 'Referral Provider';
          userData.permissions = ['referral-provider', 'dealrooms', 'public', 'lms', 'blog', 'community'];
        } else if (email.toLowerCase().includes('specialist')) {
          userData.role = 'Specialist';
          userData.name = 'Specialist User';
          userData.permissions = ['specialist', 'dealrooms', 'public', 'lms', 'blog', 'community'];
        } else {
          // Default user gets basic access
          userData.role = 'User';
          userData.name = 'Basic User';
          userData.permissions = ['public', 'lms', 'blog', 'community'];
        }

        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        setLoading(false);
        resolve(userData);
      }, 1000);
    });
  };

  const logout = () => {
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (updatedData) => {
    const newUserData = { ...user, ...updatedData };
    setUser(newUserData);
    localStorage.setItem('user', JSON.stringify(newUserData));
  };

  // Helper function to check if user has specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'SuperAdmin') return true;
    return user.permissions?.includes(permission) || false;
  };

  // Helper function to check if user can access a portal
  const canAccessPortal = (portalId) => {
    if (!user) return false;
    if (user.role === 'SuperAdmin') return true;
    return user.permissions?.includes(portalId) || false;
  };

  const value = { 
    user, 
    loading, 
    login, 
    logout, 
    updateUser, 
    hasPermission, 
    canAccessPortal 
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};